import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import { useAuth } from '../../hooks/useAuth';
import { adminShellBackgroundSx } from '../../components/admin/adminVisuals';
import { adminLoginPath } from '../../config/adminPath';

export default function AdminSetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token')?.trim() || '';
  const { updatePassword } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(
    token ? null : 'Este link de senha está incompleto. Solicite um novo link na tela de login.'
  );

  const canSubmit = useMemo(
    () => Boolean(token && password.length >= 8 && password === confirmPassword),
    [confirmPassword, password, token]
  );

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) {
      setFeedback('Informe uma senha com pelo menos 8 caracteres e confirme o mesmo valor.');
      return;
    }

    setIsSubmitting(true);
    setFeedback(null);
    const error = await updatePassword(password, token);
    setIsSubmitting(false);
    if (error) {
      setFeedback(error);
      return;
    }

    navigate(adminLoginPath, {
      replace: true,
      state: { passwordUpdated: true },
    });
  };

  return (
    <Box sx={{ ...adminShellBackgroundSx, display: 'grid', placeItems: 'center', px: 2, py: 6 }}>
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 580,
          borderRadius: 5,
          p: { xs: 3, md: 4 },
          border: '1px solid rgba(15, 23, 42, 0.08)',
          boxShadow: '0 28px 60px rgba(15, 23, 42, 0.08)',
          background:
            'radial-gradient(circle at top right, rgba(14, 165, 233, 0.12), transparent 34%), rgba(255,255,255,0.94)',
        }}
      >
        <Stack spacing={3}>
          <Stack spacing={1.5}>
            <Box
              sx={{
                width: 52,
                height: 52,
                display: 'grid',
                placeItems: 'center',
                borderRadius: 3,
                bgcolor: 'rgba(37, 99, 235, 0.12)',
                color: '#1d4ed8',
              }}
            >
              <VerifiedUserRoundedIcon />
            </Box>
            <Typography variant="h4" fontWeight={900} sx={{ color: '#16324f' }}>
              Definir senha
            </Typography>
            <Typography color="text.secondary">
              Conclua o convite ou a recuperação definindo sua nova senha administrativa.
            </Typography>
          </Stack>

          {feedback && <Alert severity="error">{feedback}</Alert>}

          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <TextField
                label="Nova senha"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                disabled={!token || isSubmitting}
                helperText="Use pelo menos 8 caracteres."
                autoComplete="new-password"
                fullWidth
              />
              <TextField
                label="Confirmar senha"
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                disabled={!token || isSubmitting}
                autoComplete="new-password"
                fullWidth
              />
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={!canSubmit || isSubmitting}
                sx={{ borderRadius: 999 }}
              >
                {isSubmitting ? 'Salvando...' : 'Salvar senha'}
              </Button>
              <Button onClick={() => navigate(adminLoginPath)} sx={{ alignSelf: 'flex-start' }}>
                Voltar ao login
              </Button>
            </Stack>
          </Box>
        </Stack>
      </Paper>
    </Box>
  );
}
